<template>
    <Card pageTitle="Cash Management">
        <div class="mb-3">
            <Button noMargin @click="$router.push(`/cash-management`)">
                <i class="fas fa-chevron-left"></i> Back To Cash Management
            </Button>
        </div>
        <div>
            <div class="safe-name" v-if="safe">{{ safe.name }}</div>
            <!-- <p class="my-3">
                Cash management is Enabled at this location. Click here to
                <router-link to="#" class="text-primary">disable</router-link>.
            </p> -->

            <div class="row" v-if="safe">
                <div class="col-md-3">
                    <DetailContainer sm title="Location Summary">
                        <div v-if="warehouseSummary">
                            <!-- Current Balance Section -->
                            <div style="margin: 15px 0; padding: 10px; background-color: #e8f5e9; border-radius: 5px; border-left: 4px solid #4caf50;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #2e7d32; font-size: 13px;">Cash on Premises</div>
                                <InfoListItem v-if="warehouseSummary.cashBreakdown" sideBySide title="Safe cash"
                                    :value="`$${warehouseSummary.cashBreakdown.safeCash}`" />
                                <InfoListItem v-if="warehouseSummary.cashBreakdown" sideBySide title="Drawer cash"
                                    :value="`$${warehouseSummary.cashBreakdown.drawerCash}`" />
                                <InfoListItem 
                                    sideBySide 
                                    :title="warehouseSummary.cashBreakdown ? 'On-premises cash' : 'Drawer cash (legacy API)'"
                                    :value="`$${warehouseSummary.currentBalance ? warehouseSummary.currentBalance.cash : warehouseSummary.totalCashAmount || 0}`" 
                                />
                                <InfoListItem 
                                    sideBySide 
                                    title="Non-cash recorded"
                                    :value="`$${warehouseSummary.currentBalance ? warehouseSummary.currentBalance.nonCash : warehouseSummary.totalNonCashAmount || 0}`" 
                                />
                            </div>

                            <!-- Historical Totals Section -->
                            <div style="margin: 15px 0; padding: 10px; background-color: #fff3e0; border-radius: 5px; border-left: 4px solid #ff9800;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #e65100; font-size: 13px;">Recorded Activity Totals</div>
                                <p class="small text-muted">Includes internal transfers and counts. Not sales, revenue or cash on hand.</p>
                                <InfoListItem 
                                    sideBySide 
                                    title="Cash" 
                                    :value="`$${warehouseSummary.historicalTotals ? warehouseSummary.historicalTotals.cash : warehouseSummary.totalCashAmount || 0}`" 
                                />
                                <InfoListItem 
                                    sideBySide 
                                    title="Non-Cash" 
                                    :value="`$${warehouseSummary.historicalTotals ? warehouseSummary.historicalTotals.nonCash : warehouseSummary.totalNonCashAmount || 0}`" 
                                />
                                <InfoListItem 
                                    sideBySide 
                                    title="Total" 
                                    :value="`$${warehouseSummary.historicalTotals ? warehouseSummary.historicalTotals.total : warehouseSummary.total || 0}`" 
                                />
                            </div>
                        </div>
                    </DetailContainer>
                </div>
                <div class="col-md-9">
                    <div>
                        <DetailContainer sm title="Store Safe">
                            <div>
                                <CashDrawerCard v-if="availableInSafe !== null" safeMode :safe="safe" :childDrawers="drawers" :availableAmount="availableInSafe" />
                                <div v-else class="text-muted">Loading safe amount...</div>
                            </div>
                        </DetailContainer>
                        <DetailContainer sm title="Cash Drawers">
                            <template #actions>
                                <CreateDrawerBtn :parent="safe.id" @created="drawers = $event" />
                            </template>
                            <div class="my-3" v-if="drawers.length">
                                <CashDrawerCard :safe="drawer" v-for="drawer of drawers" :key="drawer.id" />
                            </div>
                            <div v-else-if="loading">Loading...</div>
                            <div v-else>No Drawers Found</div>
                        </DetailContainer>
                    </div>
                </div>
            </div>
            <div v-else>Loading...</div>
        </div>
    </Card>
</template>

<script>
import { http } from "@/utils/http"
import Paginator from "../../components/other/Paginator.vue"
import Card from "../../components/UI/Card.vue"
import CashManagementFetchApi from "@/utils/fetch/cash-management-fetch-api"
import CustomTable from "../../components/layout/CustomTable.vue"
import DetailContainer from "../../components/layout/DetailContainer.vue"
import InfoListItem from "../../components/UI/InfoListItem.vue"
import Button from "../../components/UI/Button.vue"
import CashDrawerCard from "../../components/cash-management/CashDrawerCard.vue"
import CreateDrawerBtn from "../../components/cash-management/CreateDrawerBtn.vue"

export default {
    data() {
        return {
            safe: null,
            drawers: [],
            loading: false,
            warehouseSummary: null,
            availableInSafe: null,
        }
    },
    components: {
        CashDrawerCard,
        Button,
        DetailContainer,
        Card,
        Paginator,
        CustomTable,
        InfoListItem,
        CreateDrawerBtn,
    },
    async mounted() {
        this.loading = true
        try {
            this.safe = await CashManagementFetchApi.fetchSafeOrDrawerDetail(this.$route.params.id)
            const { result } = await CashManagementFetchApi.fetchWarehouseSummary(this.safe.warehouseId)
            this.warehouseSummary = result
            
            // Debug warehouse summary if query parameter is present
            if (this.$route.query.debug === 'true') {
                console.log('=== WAREHOUSE SUMMARY DEBUG ===')
                console.log('Safe:', this.safe)
                console.log('Warehouse ID:', this.safe.warehouseId)
                console.log('Warehouse Summary:', result)
                console.log('=== END DEBUG ===')
            }
            
            // Fetch child drawers
            this.drawers = await CashManagementFetchApi.fetchSafeOrDrawers(this.$route.params.id)

            // Fetch payment modes to identify cash mode
            const modesRes = await http.get("/mode-of-payments")
            const cashMode = modesRes.data.result.find(m => m.name.toLowerCase() === "cash")

            // Fetch expected amounts for the parent safe to compute "Available in Safe"
            // COVA-STYLE: Only sum CASH amounts, not all payment modes
            const expected = await CashManagementFetchApi.fetchExpectedAmounts(this.$route.params.id)
            if (Array.isArray(expected) && cashMode) {
                // Filter for cash only
                const cashOnly = expected.filter(item => item.modeOfPaymentId === cashMode.id)
                const sum = cashOnly.reduce((acc, item) => acc + Number(item?.value || 0), 0)
                // Round to 2 decimals for display consistency
                this.availableInSafe = Math.round(sum * 100) / 100
            } else {
                this.availableInSafe = 0
            }
        } catch (err) {
            console.log("error in fetching data", err)
        }
        this.loading = false
    },
    methods: {
        async handleCreateDrawer() {
            await CashManagementFetchApi.create(this.$route.params.id)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.safe-name {
    font-size: 20px;
    margin-bottom: $sectionGap / 2;
}

/* Tokenize panel look */
:deep(.detail-container) {
    border-radius: $cardRadius;
    box-shadow: $cardShadow;
}

/* Right-align money columns in any table */
::deep(table) {
  thead th:nth-child(2),
  thead th:nth-child(3) {
    text-align: right;
  }
  tbody td:nth-child(2),
  tbody td:nth-child(3) {
    text-align: right;
  }
}
</style>
